import React, { useCallback, useEffect, useState, useMemo } from 'react';
import SectionIndicatorStage from '../ImpactIndicator/SectionIndicatorStage';
import {
  getIndicatorById,
  IndicatorByService,
  IndicatorStagesProps,
  INDICATOR_STAGES_KEY,
  IndicatorSectionKey,
} from '../ImpactIndicator/utils';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { getIndicatorsUpdatedText } from '../ImpactIndicator/api';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import IndicatorStageRender from '../ImpactIndicator/IndicatorStageRender';
import ImpactSummary from '../ImpactSummary/ImpactSummary';
import { scrollToTop } from 'shared/utils/pageUtils';
import { parsePeriodValuesString, ReportPeriod } from '../SectionPeriod/utils';
import { GuidanceTextResponse } from '../utils';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import StaledReport, { SectionAnswers } from './StaledReport';
import { arrayUniq } from 'shared/utils/arrayUtils';

export const getSuccessCriteriaIndicators = (
  requestState: RequestState<{
    allReports: [
      {
        dbId: number;
        results: IndicatorWithUpdatedText[];
      },
    ];
  }>,
) => {
  if (requestState.type !== 'REQUEST_SUCCESS') return undefined;
  const indicators: { [x: number]: IndicatorWithUpdatedText } = {};
  requestState.data.allReports[0].results.forEach(r => {
    r.seen = r.seen || false;
    indicators[r.indicatorId] = r;
  });
  return { indicators, indicatorIds: Object.keys(indicators) };
};

export const addSuccessCriteriaToIndicators = ({
  indicatorIds,
  indicators,
  actEnabled,
  capacityDevelopmentEnabled,
  learnEnabled,
  currentState,
}: {
  indicatorIds: (string | number)[];
  currentState: IndicatorByService;
  indicators: { [x: number]: IndicatorWithUpdatedText };
  capacityDevelopmentEnabled: boolean;
  learnEnabled: boolean;
  actEnabled: boolean;
}): IndicatorByService => {
  indicatorIds.forEach(id => {
    const element: IndicatorWithUpdatedText =
      indicators[id as number] || indicators[Number(id)];
    const sectionKey = INDICATOR_STAGES_KEY[element.indicator.stageId];
    const currentIndicators = currentState[sectionKey];
    const index = currentIndicators.findIndex(x => x.id.toString() === id);
    if (index === -1) {
      currentIndicators.push({
        id: element.indicatorId,
        definition: element.indicator.definition,
        name: element.indicator.name,
        stageId: element.indicator.stageId,
        stgName: element.indicator.stgName,
        customizable: true,
        updatedText: element.successCriteria,
        seen: element.seen,
        version: element.indicator.version,
        unit: element.indicator.unit,
        definitionStart: element.indicator.definitionStart,
        definitionEnd: element.indicator.definitionEnd,
        initialForecastStart: element.indicator.initialForecastStart,
        initialForecastEnd: element.indicator.initialForecastEnd,
        initialMeasurementStart: element.indicator.initialMeasurementStart,
        initialMeasurementEnd: element.indicator.initialMeasurementEnd,
        finalForecastStart: element.indicator.finalForecastStart,
        impactReceiptText: element.indicator.impactReceiptText,
        outcome: element.indicator.outcome,
        forecastOutcome: element.indicator.forecastOutcome,
        finalOutcome: element.indicator.finalOutcome,
        finalForecastEnd: element.indicator.finalForecastEnd,
        finalMeasurementStart: element.indicator.finalMeasurementStart,
        finalMeasurementEnd: element.indicator.finalMeasurementEnd,
        isHuman: element.indicator.isHuman,
      });
    } else {
      currentIndicators[index].seen =
        element.seen || Boolean(element.successCriteria);
      currentIndicators[index].updatedText = element.successCriteria;
    }
  });
  if (!capacityDevelopmentEnabled) {
    currentState.capacityDevelopment = [];
  }
  if (!learnEnabled) {
    currentState.learn = [];
  }
  if (!actEnabled) {
    currentState.act = [];
  }
  return currentState;
};

const getIndicatorsBySection = (
  initialData: string[],
  allIndicators: IndicatorByService,
): { [x in IndicatorSectionKey]: string[] } => {
  const result: { [x in IndicatorSectionKey]: string[] } = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };
  for (const i of initialData) {
    if (allIndicators.succeed.find(x => x.name === i)) {
      result.succeed.push(i);
    }
    if (allIndicators.reach.find(x => x.name === i)) {
      result.reach.push(i);
    }
    if (allIndicators.learn.find(x => x.name === i)) {
      result.learn.push(i);
    }
    if (allIndicators.act.find(x => x.name === i)) {
      result.act.push(i);
    }
    if (allIndicators.catalyzed.find(x => x.name === i)) {
      result.catalyzed.push(i);
    }
    if (allIndicators.capacityDevelopment.find(x => x.name === i)) {
      result.capacityDevelopment.push(i);
    }
  }
  return result;
};

export const getInitialImpactSummaryValues = (
  initial: IndicatorStagesProps['indicatorSummary'],
  selectedImpactIndicators: string[],
  selectedCapacityIndicators: string[],
  selectedReachIndicators: string[],
  indicators: IndicatorByService,
) => {
  let initialValues: string[] = [];
  if (Array.isArray(initial)) {
    initialValues = initial;
  } else {
    for (const i of Object.keys(initial) as IndicatorSectionKey[]) {
      for (const j of initial[i]) {
        initialValues.push(j.name);
      }
    }
  }
  const i = getIndicatorsBySection(initialValues, indicators);

  return arrayUniq([
    ...i.learn,
    ...i.act,
    ...selectedImpactIndicators,
    ...selectedReachIndicators,
    ...selectedCapacityIndicators,
  ]);
};

export interface IndicatorWithUpdatedText {
  dbId: number;
  description: string;
  seen: boolean;
  indicatorId: number;
  successCriteria: string;
  indicator: {
    stageId: number;
    name: string;
    definition: string;
    stgName: string;
    version: 'v4' | null;
    unit: string;
    definitionStart: string;
    definitionEnd: string;
    initialForecastStart: string;
    initialForecastEnd: string;
    initialMeasurementStart: string;
    initialMeasurementEnd: string;
    impactReceiptText?: string;
    outcome?: string;
    forecastOutcome?: string;
    finalOutcome?: string;
    finalForecastStart: string;
    finalForecastEnd: string;
    isHuman: boolean;
    finalMeasurementStart: string;
    finalMeasurementEnd: string;
  };
}

const IndicatorStages: React.FC<IndicatorStagesProps & {
  guidanceText: GuidanceTextResponse | undefined;
  reportDates: string | undefined;
  selectedReachIndicators: string[];
  selectedCapacityIndicators: string[];
  selectedSocialImpactIndicators: string[];
  sectionAnswers?: SectionAnswers[];
  setReportBuilderValues?: React.Dispatch<
    React.SetStateAction<{ [id: number]: string | undefined }>
  >;
}> = props => {
  // const { capacityDevelopmentEnabled } = props;
  const reportPeriod: ReportPeriod = props.reportDates
    ? typeof props.reportDates === 'string'
      ? parsePeriodValuesString(props.reportDates)
      : props.reportDates
    : {
        card: undefined,
        startDate: undefined,
        endDate: undefined,
      };
  const capacityDevelopmentEnabled =
    props.selectedCapacityIndicators.length > 0;
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [defaultIndicators, setDefaultIndicators] = useState<
    IndicatorByService
  >(
    getIndicatorById(
      props.indicators,
      props.service?.dbId,
      capacityDevelopmentEnabled,
      props.learnEnabled,
      props.actEnabled,
    ),
  );

  const initialValues = useMemo(
    () =>
      getInitialImpactSummaryValues(
        props.indicatorSummary,
        props.selectedSocialImpactIndicators,
        props.selectedCapacityIndicators,
        props.selectedReachIndicators,
        defaultIndicators,
      ),
    [
      props.indicatorSummary,
      props.selectedReachIndicators,
      props.selectedCapacityIndicators,
      props.selectedSocialImpactIndicators,
    ],
  );

  useEffect(() => {
    scrollToTop();
  }, [props]);

  const client = useClient();
  const [indicatorsWithUpdatedTextRequest, makeRequest] = useGraphQLRequest<{
    allReports: [
      {
        dbId: number;
        results: IndicatorWithUpdatedText[];
      },
    ];
  }>();

  useEffect(() => {
    makeRequest(getIndicatorsUpdatedText(client!, props.reportId));
  }, [props.reportId]);

  useEffect(() => {
    const response = getSuccessCriteriaIndicators(
      indicatorsWithUpdatedTextRequest,
    );
    if (response === undefined) return;
    const { indicators, indicatorIds } = response;
    if (indicatorIds.length === 0) {
      setLoadingData(false);
      return;
    }
    setDefaultIndicators(s => {
      return addSuccessCriteriaToIndicators({
        actEnabled: props.actEnabled,
        capacityDevelopmentEnabled,
        currentState: { ...s },
        indicatorIds,
        indicators,
        learnEnabled: props.learnEnabled,
      });
    });
    setLoadingData(false);
  }, [
    indicatorsWithUpdatedTextRequest,
    capacityDevelopmentEnabled,
    setDefaultIndicators,
  ]);

  const [showDetails, setShowDetails] = useState<boolean>(false);
  const handleDetailOpen = useCallback(() => {
    setShowDetails(true);
  }, [setShowDetails]);
  const handleDetailClose = useCallback(() => {
    setShowDetails(false);
  }, [setShowDetails]);

  if (!props.service?.dbId) {
    return (
      <StaledReport
        reportId={props.reportId}
        sectionAnswers={props.sectionAnswers}
      />
    );
  }

  if (
    loadingData ||
    indicatorsWithUpdatedTextRequest.type === 'REQUEST_INIT' ||
    indicatorsWithUpdatedTextRequest.type === 'REQUEST_START'
  ) {
    return <TrueImpactLoading />;
  }

  return (
    <SectionIndicatorStage
      {...props}
      reportPeriod={reportPeriod}
      programName={props.programName || 'your program'}
      indicatorSummary={initialValues}
      maxWidth={560}
      capacityDevelopmentEnabled={capacityDevelopmentEnabled}
      isReportPassed={props.isReportPassed}
      defaultIndicators={defaultIndicators}
      handleDetailOpen={handleDetailOpen}
      handleDetailClose={handleDetailClose}
      RenderComponent={showDetails ? ImpactSummary : IndicatorStageRender}
      sectionAnswers={props?.sectionAnswers}
    />
  );
};

export default React.memo(IndicatorStages);
